import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  TDeliverable,
  TDeliverableForm,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { DeliverableListService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.service';
import { ViewportService } from '@core/services/viewport.service';
import { CtaAnalyticsDetailsKpisTileComponent } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpis-tile.component';
import { CtaAnalyticsDetailsComponent } from '@cta-analytics-details/cta-analytics-details.component';
import { IDeliverableSelectedEvent } from '@cta-analytics-details/cta-analytics-details.interface';
import { DeliverableAnalyticsContentComponent } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-analytics-content.component';
import { EAnalyticsStatus, EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus, EDeliverableType } from '@enums/campaign.enum';
import { ICtaAnalyticsApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICtaResult } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICampaignExportApi } from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { ICreator } from '@pages/creators/creators.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ISkeletonColumn } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { SocialContentFilesComponent } from '@shared/components/social-content-files/social-content-files.component';
import { SocialContentTileComponent } from '@shared/components/social-content-tile/social-content-tile.component';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { AnalyticsFormatterFactory } from './cta-analytics-details/analytics-formatters/analytics-formatter-factory';
import { IDeliverableCard } from './cta-analytics-details/cta-analytics-details-deliverable-card/cta-analytics-details-deliverable-card.interface';
import { IKpiCard } from './cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { CtaAnalyticsExecutionService } from './cta-analytics-execution.service';
import { ICtaAnalyticsData } from './cta-analytics-table.interface';

interface ICtaAnalyticsDetailPanelContext {
  deliverableCardData: IDeliverableCard;
  deliverableForm: FormGroup<TDeliverableForm>;
  deliverableId: string;
  deliverableType: EDeliverableType;
  executionAnalytics: ICtaAnalyticsData | null;
}

@Component({
  imports: [
    NgClass,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    TranslateModule,
    MatButtonModule,
    MatTooltipModule,
    CtaAnalyticsDetailsComponent,
    SocialContentTileComponent,
    CtaAnalyticsDetailsKpisTileComponent,
    DeliverableAnalyticsContentComponent,
    IconComponent,
    SocialContentFilesComponent,
  ],
  selector: 'app-cta-analytics-table',
  styleUrls: ['./cta-analytics-table.component.scss'],
  templateUrl: './cta-analytics-table.component.html',
})
export class CtaAnalyticsTableComponent {
  public readonly adminMode = input<boolean>(false);
  public readonly analyticsNode = input.required<IAnalyticsNode>();
  public readonly campaignExport = input<ICampaignExportApi | null>(null);
  public readonly briefingId = input<string | null>(null);
  public readonly campaignProcessStatus = input<EProcessStatus>();
  public readonly campaignStatus = input<ECampaignStatus>();
  public readonly creator = input.required<ICreator>();
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly ctaResult = input.required<ICtaResult>();
  public readonly exportMode = input<boolean>(false);
  // Each change returns to the overview panel (the mobile briefing sheet's top-bar "Back to overview").
  public readonly backToOverviewRequest = input<number>(0);

  public readonly ctaExecutionChange = output<string>();
  public readonly deliverableSelected = output<IDeliverableSelectedEvent>();
  public readonly overviewRequested = output<void>();
  public readonly detailViewChange = output<boolean>();

  public readonly selectedDeliverableId = input<string | null>(null);
  public readonly selectedDeliverableType = input<EDeliverableType | null>(null);
  public readonly selectedDeliverableExecutionAnalytics = input<ICtaAnalyticsData | null>(null);

  public readonly data = signal<ICtaAnalyticsData | null>(null);
  public readonly deliverableCardData = signal<IDeliverableCard[]>([]);
  public readonly deliverableCardDataMap = signal<Map<string, IDeliverableCard>>(new Map());
  public readonly kpiCardDataForKpisTile = signal<IKpiCard[]>([]);
  public readonly kpiGoalsKpiCards = signal<IKpiCard[]>([]);
  public readonly loading = signal<boolean>(true);
  public readonly socialContentIdForFiles = computed(() => {
    return this.data()?.socialContentId ?? this.analyticsNode().content?.socialContent?.id ?? null;
  });

  public readonly deliverableFormsMap = computed(() => {
    if (!this.data()) {
      return new Map<string, FormGroup<TDeliverableForm>>();
    }

    return this.getDeliverableFormsMap(this.data()!.ctaAnalytics!);
  });
  public readonly selectedDeliverableForm = computed<FormGroup<TDeliverableForm> | null>(() => {
    const deliverableId = this.selectedDeliverableId();

    if (!deliverableId) {
      return null;
    }

    return this.deliverableFormsMap().get(deliverableId) ?? null;
  });
  public readonly canShowDetail = computed(() => {
    return (
      !!this.selectedDeliverableId() &&
      !!this.selectedDeliverableType() &&
      !!this.selectedDeliverableForm() &&
      !!this.selectedDeliverableExecutionAnalytics()
    );
  });
  public readonly panelView = signal<'overview' | 'detail'>('overview');
  public readonly navigationDirection = signal<'forward' | 'back'>('forward');
  public readonly detailPanelContext = signal<ICtaAnalyticsDetailPanelContext | null>(null);

  public readonly EAnalyticsStatus = EAnalyticsStatus;
  public readonly EMatchingType = EMatchingType;
  public readonly skeletonThumbnailColumns: ISkeletonColumn[] = [
    {
      borderRadius: '8px',
      height: '64px',
      rowMargin: '0',
      rowPadding: '0',
      type: 'text',
      width: '112px',
    },
  ];
  public readonly skeletonThumbnailRows = 1;

  public imageLoadingStates = new Map<string, boolean>();

  private readonly ctaAnalyticsExecutionService = inject(CtaAnalyticsExecutionService);
  private readonly campaignAnalyticsUtilitiesService = inject(CampaignAnalyticsUtilitiesService);
  private readonly deliverableListService = inject(DeliverableListService);
  private readonly analyticsFormatterFactory = inject(AnalyticsFormatterFactory);
  private readonly translate = inject(TranslateService);
  private readonly viewportService = inject(ViewportService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  // The mobile briefing sheet stacks both columns in one page scroll instead of side-by-side scroll panels.
  public readonly mobileBriefing = computed(() => this.creatorBriefingMode() && this.viewportService.isMobile());

  @ViewChild('overviewPanel')
  private readonly overviewPanel?: ElementRef<HTMLDivElement>;

  @ViewChild('detailPanel')
  private readonly detailPanel?: ElementRef<HTMLDivElement>;

  constructor() {
    this.createSheetNavigationEffects();

    effect(() => {
      if (this.analyticsNode() && this.creator() !== undefined) {
        this.data.set(this.createTableData(this.analyticsNode()));
      }
    });

    effect(() => {
      const execution = this.data();
      const platformFeature = execution?.platformFeature;

      if (!platformFeature) {
        this.kpiCardDataForKpisTile.set([]);
        this.kpiGoalsKpiCards.set([]);
      }

      if (platformFeature) {
        const formatter = this.analyticsFormatterFactory.createFormatter(platformFeature, execution, this.translate);
        this.deliverableCardData.set(formatter.generateDeliverableCardData());

        const deliverableCardDataMap = new Map<string, IDeliverableCard>();
        this.deliverableCardData().forEach((deliverable) => {
          deliverableCardDataMap.set(deliverable.id, deliverable);
        });

        this.deliverableCardDataMap.set(deliverableCardDataMap);

        const rawKpiCards = formatter.generateKpiCardData({
          ignoreExpectedKpi: false,
          includeKpisWithNullValue: true,
        });

        this.kpiGoalsKpiCards.set(rawKpiCards.filter((kpi) => kpi.targetValue !== null));

        this.kpiCardDataForKpisTile.set(
          rawKpiCards.filter((kpi) => kpi.value !== null).map((kpi) => ({ ...kpi, targetValue: null })),
        );
      }
    });

    effect(() => {
      if (!this.canShowDetail()) {
        return;
      }

      const deliverableId = this.selectedDeliverableId();
      const deliverableType = this.selectedDeliverableType();
      const deliverableForm = this.selectedDeliverableForm();
      const executionAnalytics = this.selectedDeliverableExecutionAnalytics();

      if (!deliverableId || !deliverableType || !deliverableForm || !executionAnalytics) {
        return;
      }

      const deliverableCardData = this.deliverableCardDataMap().get(deliverableId);

      if (!deliverableCardData) {
        return;
      }

      this.detailPanelContext.set({
        deliverableCardData,
        deliverableForm,
        deliverableId,
        deliverableType,
        executionAnalytics,
      });
      this.navigationDirection.set('forward');
      this.panelView.set('detail');
      this.resetPanelScroll('detail');
    });
  }

  public onImageLoaded(executionId: string): void {
    if (executionId) {
      this.imageLoadingStates.set(executionId, false);
    }
  }

  public isImageLoading(executionId: string): boolean {
    return this.imageLoadingStates.get(executionId) ?? true;
  }

  public onBackToOverview(): void {
    this.navigationDirection.set('back');
    this.panelView.set('overview');
    this.overviewRequested.emit();
    this.resetPanelScroll('overview');
    window.setTimeout(() => {
      if (this.panelView() === 'overview' && !this.canShowDetail()) {
        this.detailPanelContext.set(null);
      }
    }, 260);
  }

  // The mobile briefing sheet shows "Back to overview" in the dialog's top bar, outside this component.
  private createSheetNavigationEffects(): void {
    effect(() => {
      this.detailViewChange.emit(this.panelView() === 'detail');
    });

    effect(() => {
      if (this.backToOverviewRequest() > 0) {
        untracked(() => this.onBackToOverview());
      }
    });
  }

  private resetPanelScroll(panel: 'overview' | 'detail'): void {
    queueMicrotask(() => {
      // On the mobile sheet the panels don't scroll themselves; the whole sheet does.
      if (this.mobileBriefing()) {
        this.elementRef.nativeElement.scrollIntoView({ block: 'start' });

        return;
      }

      const container = panel === 'overview' ? this.overviewPanel?.nativeElement : this.detailPanel?.nativeElement;
      container?.scrollTo({ top: 0 });
    });
  }

  private createTableData(analyticsNode: IAnalyticsNode): ICtaAnalyticsData | null {
    return this.ctaAnalyticsExecutionService.createExecutionData(
      analyticsNode,
      this.ctaResult(),
      this.campaignProcessStatus() ?? EProcessStatus.processing,
      this.campaignStatus() ?? ECampaignStatus.draft,
    );
  }

  private getDeliverableFormsMap(ctaAnalytics: ICtaAnalyticsApi): Map<string, FormGroup<TDeliverableForm>> {
    if (!ctaAnalytics) {
      return new Map<string, FormGroup<TDeliverableForm>>();
    }

    const deliverables = this.campaignAnalyticsUtilitiesService.getDeliverablesAsSql(ctaAnalytics);

    return deliverables.reduce((acc, deliverable) => {
      const form = this.deliverableListService.createDeliverable(
        deliverable.type,
        deliverable as TDeliverable,
        undefined,
        true,
      )!;

      acc.set(deliverable.id, form);

      return acc;
    }, new Map<string, FormGroup<TDeliverableForm>>());
  }
}
